import React from 'react';
import { useFinancialPositionQuery } from '@/api';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { FinancialPositionMetrics } from '@/api/models';

/**
 * The one read behind all four cards.
 *
 * Each card calls this with the slice it wants. TanStack dedupes on the query
 * key, so the four self-served cards still own their own data — every one is
 * mountable anywhere on its own — while the screen makes a single request and
 * every card describes the same moment in time.
 *
 * The consumer contract matches every other self-served widget in the app:
 * `{ data, error, isLoading, onRetry }`, where the queryFn never rejects and the
 * error is the envelope's, not TanStack's.
 */
export const useOverviewMetric = <Key extends keyof FinancialPositionMetrics>(
    key: Key,
) => {
    const query = useFinancialPositionQuery();

    const metric = React.useMemo<FinancialPositionMetrics[Key] | null>(
        () => (query.data?.success ? query.data.response.metrics[key] : null),
        [query.data, key],
    );

    const error = React.useMemo<ErrorResponse | null>(
        () =>
            query.data && !query.data.success
                ? (query.data.error ?? null)
                : null,
        [query.data],
    );

    const onRetry = React.useCallback(() => {
        void query.refetch();
    }, [query]);

    return {
        metric,
        error,
        isLoading: query.isPending,
        /**
         * What this metric is waiting for. Empty once it is computed — and read
         * even while pending, because it is what picks the empty card's
         * sentence.
         */
        missing: metric?.missing ?? [],
        onRetry,
    } as const;
};
